import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import AuthShell from "./AuthShell";
import { useAuth } from "../../context/AuthContext";
import api, { apiErr } from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";

export default function SignIn() {
  const { login, setUser } = useAuth();
  const nav = useNavigate();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState("password");
  const [code, setCode] = useState("");

  const routeUser = (u) => {
    if (u.role === "admin") nav("/admin");
    else if (u.must_confirm_profile || !u.onboarding_complete) nav("/onboarding");
    else nav("/app");
  };

  const submitCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login-code", { code });
      setUser(data);
      routeUser(data);
    } catch (err) { toast.error(apiErr(err)); }
    finally { setLoading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(identifier, password);
      routeUser(u);
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell title="Welcome back" subtitle="Sign in to your COMMITMENTT account."
      footer={<>New here? <Link to="/register" className="text-mint underline underline-offset-4">Create your account</Link>.</>}>
      <div className="flex gap-2 mb-5 p-1 rounded-full bg-secondary">
        <button type="button" onClick={() => setMode("password")} className={`flex-1 text-sm py-2 rounded-full transition-colors ${mode === "password" ? "bg-white text-ink shadow-sm" : "text-slatey"}`} data-testid="mode-password">Password</button>
        <button type="button" onClick={() => setMode("code")} className={`flex-1 text-sm py-2 rounded-full transition-colors ${mode === "code" ? "bg-white text-ink shadow-sm" : "text-slatey"}`} data-testid="mode-code">Access code</button>
      </div>
      {mode === "password" ? (
      <form onSubmit={submit} className="space-y-4">
        <div><Label htmlFor="signin-email" className="text-ink">Email or mobile number</Label><Input id="signin-email" name="username" autoComplete="username" required type="text" value={identifier} onChange={(e) => setIdentifier(e.target.value)} className="mt-1.5 rounded-xl" data-testid="signin-email" /></div>
        <div>
          <div className="flex justify-between items-center"><Label htmlFor="signin-password" className="text-ink">Password</Label>
            <Link to="/reset-password" className="text-xs text-mint" data-testid="signin-forgot">Forgot password?</Link></div>
          <Input id="signin-password" name="password" autoComplete="current-password" required type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1.5 rounded-xl" data-testid="signin-password" />
        </div>
        <Button type="submit" disabled={loading} className="w-full rounded-full bg-navy text-white hover:bg-navy-light h-11" data-testid="signin-submit">
          {loading ? "Signing in…" : "Sign In"}
        </Button>
      </form>
      ) : (
      <form onSubmit={submitCode} className="space-y-4">
        <div><Label htmlFor="signin-code" className="text-ink">Access code</Label>
          <Input id="signin-code" name="access-code" required value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} className="mt-1.5 rounded-xl font-mono tracking-wider" placeholder="Enter your login code" data-testid="signin-code" /></div>
        <Button type="submit" disabled={loading} className="w-full rounded-full bg-navy text-white hover:bg-navy-light h-11" data-testid="signin-code-submit">
          {loading ? "Signing in…" : "Log in with code"}
        </Button>
        <p className="text-xs text-muted-foreground">Admin-generated codes are single-use and expire within 24 hours.</p>
      </form>
      )}
    </AuthShell>
  );
}
